import type { JSX } from 'react';

import type {
  DetectionMode,
  DeviceExtensions,
  HoneywellConfig,
  SamsungConfig,
  ZebraConfig,
} from '@schema/device.js';

import type { BuilderStore } from '../state.js';

import { SelectField, TextField, ToggleField } from './Field.js';

export interface OemDeviceEditorProps {
  store: BuilderStore;
}

const DETECTION_OPTIONS = [
  { value: 'auto',           label: 'Auto-detect (production)' },
  { value: 'forceZebra',     label: 'Force Zebra (QA)' },
  { value: 'forceSamsung',   label: 'Force Samsung (QA)' },
  { value: 'forceHoneywell', label: 'Force Honeywell (QA)' },
  { value: 'none',           label: 'Disable OEM modules' },
] as const;

const SAMSUNG_KEYS = ['bixby', 'volumeUp', 'volumeDown', 'power'] as const;

/**
 * OEM device extensions editor (Enterprise feature: `oem.zebra` / `oem.samsung`
 * / `oem.honeywell`). The whole block is optional — the top-level toggle
 * adds/removes `config.device` outright so configs stay tidy when unused.
 *
 * `forceZebra/Samsung/Honeywell` detection modes are validated by Zod
 * against the matching sub-block being defined; the editor enforces the
 * happy path by auto-enabling the matching sub-block when the user picks a
 * force-mode, so the user doesn't see a refine error unless they import
 * a broken config.
 */
export function OemDeviceEditor({ store }: OemDeviceEditorProps): JSX.Element {
  const device = store.config.device;

  function enable(): void {
    store.patchDevice({});
  }
  function disable(): void {
    store.patchDevice(undefined);
  }
  function patch(next: DeviceExtensions): void {
    store.patchDevice(next);
  }

  if (device === undefined) {
    return (
      <div className="editor">
        <header className="editor__head">
          <h2 className="editor__title">OEM device extensions</h2>
          <p className="editor__sub">
            Vendor-specific behaviors (DataWedge, Knox, EZConfig). Requires the
            matching <strong>oem.&lt;vendor&gt;</strong> Enterprise entitlement
            at runtime; the runtime ignores unentitled blocks.
          </p>
        </header>
        <fieldset className="editor__group">
          <legend className="editor__group-label">OEM extensions</legend>
          <p className="editor__hint">
            Not configured. Enable to expose Zebra / Samsung / Honeywell knobs.
          </p>
          <button type="button" className="add-tile__btn" onClick={enable}>
            + Enable OEM block
          </button>
        </fieldset>
      </div>
    );
  }

  function setDetection(mode: DetectionMode): void {
    // Auto-enable the matching sub-block so the Zod refine doesn't fire
    // the moment the user picks a force mode.
    const next: DeviceExtensions = { ...device, detection: mode };
    if (mode === 'forceZebra' && next.zebra === undefined) next.zebra = {};
    if (mode === 'forceSamsung' && next.samsung === undefined) next.samsung = {};
    if (mode === 'forceHoneywell' && next.honeywell === undefined) next.honeywell = {};
    patch(next);
  }

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">OEM device extensions</h2>
        <p className="editor__sub">
          Vendor-specific behaviors. The runtime ignores blocks the device's
          entitlement doesn't cover, so it's safe to configure all three.
        </p>
      </header>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Detection</legend>
        <SelectField
          label="Detection mode"
          hint="Auto is production; force modes pin a vendor for QA."
          value={device.detection ?? 'auto'}
          options={DETECTION_OPTIONS}
          onChange={(v) => setDetection(v as DetectionMode)}
        />
        <button
          type="button"
          className="builder__btn builder__btn--ghost"
          onClick={disable}
          title="Remove the entire device block from this config"
        >
          Remove OEM block
        </button>
      </fieldset>

      <ZebraPanel
        config={device.zebra}
        forceMode={device.detection === 'forceZebra'}
        onChange={(next) => patch({ ...device, zebra: next })}
        onToggle={(on) => {
          if (on) patch({ ...device, zebra: device.zebra ?? {} });
          else {
            const { zebra: _z, ...rest } = device;
            void _z;
            patch(rest);
          }
        }}
      />
      <SamsungPanel
        config={device.samsung}
        forceMode={device.detection === 'forceSamsung'}
        onChange={(next) => patch({ ...device, samsung: next })}
        onToggle={(on) => {
          if (on) patch({ ...device, samsung: device.samsung ?? {} });
          else {
            const { samsung: _s, ...rest } = device;
            void _s;
            patch(rest);
          }
        }}
      />
      <HoneywellPanel
        config={device.honeywell}
        forceMode={device.detection === 'forceHoneywell'}
        onChange={(next) => patch({ ...device, honeywell: next })}
        onToggle={(on) => {
          if (on) patch({ ...device, honeywell: device.honeywell ?? {} });
          else {
            const { honeywell: _h, ...rest } = device;
            void _h;
            patch(rest);
          }
        }}
      />
    </div>
  );
}

/* ───────── Zebra ───────── */

function ZebraPanel({
  config,
  forceMode,
  onChange,
  onToggle,
}: {
  config: ZebraConfig | undefined;
  forceMode: boolean;
  onChange: (next: ZebraConfig) => void;
  onToggle: (on: boolean) => void;
}): JSX.Element {
  return (
    <fieldset className="editor__group oem-panel">
      <legend className="editor__group-label">
        Zebra (MX / DataWedge / EMDK){' '}
        {forceMode && <span className="oem-panel__pin">forced</span>}
      </legend>
      <ToggleField
        label="Enable Zebra block"
        hint={forceMode ? 'Required by detection mode.' : 'Adds the device.zebra section.'}
        value={config !== undefined}
        onChange={onToggle}
      />
      {config !== undefined && (
        <>
          <TextField
            label="DataWedge profile"
            hint="Named DataWedge profile applied to scanner input."
            value={config.dataWedgeProfile ?? ''}
            onChange={(v) => {
              if (v.length > 0) onChange({ ...config, dataWedgeProfile: v });
              else {
                const { dataWedgeProfile: _o, ...rest } = config;
                void _o;
                onChange(rest);
              }
            }}
          />
          <ToggleField
            label="Scanner LED feedback"
            hint="Pulse the scanner LED on a successful decode."
            value={config.scannerFeedback ?? false}
            onChange={(v) => onChange({ ...config, scannerFeedback: v })}
          />
          <SelectField
            label="Hot-swap mode"
            hint="Zebra battery hot-swap UI behavior."
            value={config.hotSwapMode ?? 'auto'}
            options={[
              { value: 'auto',     label: 'Auto' },
              { value: 'manual',   label: 'Manual' },
              { value: 'disabled', label: 'Disabled' },
            ]}
            onChange={(v) => onChange({ ...config, hotSwapMode: v })}
          />
          <KeyRemapEditor
            value={config.keyRemap ?? {}}
            onChange={(next) => {
              if (Object.keys(next).length === 0) {
                const { keyRemap: _o, ...rest } = config;
                void _o;
                onChange(rest);
              } else {
                onChange({ ...config, keyRemap: next });
              }
            }}
          />
        </>
      )}
    </fieldset>
  );
}

/* ───────── Samsung ───────── */

function SamsungPanel({
  config,
  forceMode,
  onChange,
  onToggle,
}: {
  config: SamsungConfig | undefined;
  forceMode: boolean;
  onChange: (next: SamsungConfig) => void;
  onToggle: (on: boolean) => void;
}): JSX.Element {
  return (
    <fieldset className="editor__group oem-panel">
      <legend className="editor__group-label">
        Samsung (Knox / DeX){' '}
        {forceMode && <span className="oem-panel__pin">forced</span>}
      </legend>
      <ToggleField
        label="Enable Samsung block"
        hint={forceMode ? 'Required by detection mode.' : 'Adds the device.samsung section.'}
        value={config !== undefined}
        onChange={onToggle}
      />
      {config !== undefined && (
        <>
          <ToggleField
            label="Show Knox attestation status"
            hint="Render attestation result in the diagnostic overlay."
            value={config.showAttestationStatus ?? false}
            onChange={(v) => onChange({ ...config, showAttestationStatus: v })}
          />
          <SelectField
            label="DeX mode"
            hint="Behavior when the device is docked."
            value={config.dexMode ?? 'standard'}
            options={[
              { value: 'kiosk',    label: 'Kiosk' },
              { value: 'standard', label: 'Standard' },
              { value: 'disabled', label: 'Disabled' },
            ]}
            onChange={(v) => onChange({ ...config, dexMode: v })}
          />
          <div className="oem-panel__group">
            <span className="field__label">Hardware key intercepts</span>
            <div className="day-pill-row">
              {SAMSUNG_KEYS.map((k) => {
                const active = (config.keyIntercepts ?? []).includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={active}
                    className={`day-pill day-pill--wide${active ? ' day-pill--active' : ''}`}
                    onClick={() => {
                      const cur = config.keyIntercepts ?? [];
                      const nextList = active ? cur.filter((x) => x !== k) : [...cur, k];
                      if (nextList.length === 0) {
                        const { keyIntercepts: _o, ...rest } = config;
                        void _o;
                        onChange(rest);
                      } else {
                        onChange({ ...config, keyIntercepts: nextList });
                      }
                    }}
                  >
                    {k}
                  </button>
                );
              })}
            </div>
            <span className="field__hint">
              Selected keys are intercepted by the launcher instead of firing
              their default OS action.
            </span>
          </div>
        </>
      )}
    </fieldset>
  );
}

/* ───────── Honeywell ───────── */

function HoneywellPanel({
  config,
  forceMode,
  onChange,
  onToggle,
}: {
  config: HoneywellConfig | undefined;
  forceMode: boolean;
  onChange: (next: HoneywellConfig) => void;
  onToggle: (on: boolean) => void;
}): JSX.Element {
  return (
    <fieldset className="editor__group oem-panel">
      <legend className="editor__group-label">
        Honeywell (EZConfig){' '}
        {forceMode && <span className="oem-panel__pin">forced</span>}
      </legend>
      <ToggleField
        label="Enable Honeywell block"
        hint={forceMode ? 'Required by detection mode.' : 'Adds the device.honeywell section.'}
        value={config !== undefined}
        onChange={onToggle}
      />
      {config !== undefined && (
        <>
          <ToggleField
            label="Scanner wedge enabled"
            hint="Honeywell scanner wedge; Zebra-equivalent semantics."
            value={config.scannerWedgeEnabled ?? false}
            onChange={(v) => onChange({ ...config, scannerWedgeEnabled: v })}
          />
          <TextField
            label="EZConfig profile"
            hint="Named EZConfig profile applied on launch."
            value={config.ezConfigProfile ?? ''}
            onChange={(v) => {
              if (v.length > 0) onChange({ ...config, ezConfigProfile: v });
              else {
                const { ezConfigProfile: _o, ...rest } = config;
                void _o;
                onChange(rest);
              }
            }}
          />
        </>
      )}
    </fieldset>
  );
}

/* ───────── Zebra key remap (Record<string,string>) ───────── */

function KeyRemapEditor({
  value,
  onChange,
}: {
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}): JSX.Element {
  const entries = Object.entries(value);
  function updateKey(oldKey: string, newKey: string): void {
    if (newKey === oldKey) return;
    const next: Record<string, string> = {};
    for (const [k, v] of entries) next[k === oldKey ? newKey : k] = v;
    onChange(next);
  }
  function updateVal(key: string, newVal: string): void {
    onChange({ ...value, [key]: newVal });
  }
  function add(): void {
    let i = 1;
    while (`KEYCODE_${i}` in value) i++;
    onChange({ ...value, [`KEYCODE_${i}`]: 'IGNORE' });
  }
  function remove(key: string): void {
    const { [key]: _omit, ...rest } = value;
    void _omit;
    onChange(rest);
  }

  return (
    <div className="key-remap">
      <div className="key-remap__head">
        <span className="field__label">Key remap</span>
        <span className="field__hint">
          Map hardware key codes to actions (per Zebra MX docs).
        </span>
      </div>
      {entries.length === 0 && (
        <p className="editor__hint">No remaps. Add a row to map a hardware key.</p>
      )}
      <ul className="key-remap__list">
        {entries.map(([k, v]) => (
          <li key={k} className="key-remap__row">
            <input
              className="field__input field__input--mono"
              value={k}
              onChange={(e) => updateKey(k, e.target.value)}
              aria-label="Key code"
              spellCheck={false}
            />
            <span className="key-remap__arrow" aria-hidden>→</span>
            <input
              className="field__input field__input--mono"
              value={v}
              onChange={(e) => updateVal(k, e.target.value)}
              aria-label="Action"
              spellCheck={false}
            />
            <button
              type="button"
              className="tile-card__btn tile-card__btn--danger"
              onClick={() => remove(k)}
              aria-label="Remove remap"
              title="Remove"
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="add-tile__btn" onClick={add}>
        + Add remap
      </button>
    </div>
  );
}
